import { useQuery } from "@tanstack/react-query";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import { useDataQueryScope } from "../hooks/useDataQueryScope";
import { listFakeWorkflowInstances, useFakeWorkflowStoreVersion } from "./fake-workflow-store";
import {
  customerStatusLabel,
  getFakeCustomerRecord,
  listFakeCustomerRecords,
  resolveSellerName,
  useFakeCustomerStoreVersion,
} from "./fake-customer-store";

type ViewValue = string | number | boolean | null;

async function queryView<T extends Record<string, ViewValue>>(view: string): Promise<T[]> {
  const { data, error } = await supabase.from(view).select("*");
  if (error) throw error;
  return (data ?? []) as T[];
}

export interface Customer360Row {
  id: string;
  displayName: string;
  kind: string;
  identifier: string;
  contact: string;
  financialStatus: string;
  riskLevel: string;
  contracts: number;
  subscriptions: number;
  agreements: number;
  openInvoices: number;
  outstandingCents: string;
  openTasks: number;
  lastEventAt?: string;
  customerOrigin?: string;
  responsibleSeller?: string;
  registrationStatus?: string;
  contractStatus?: string;
  accessStatus?: string;
  benefitStatus?: string;
  birthDate?: string;
  address?: string;
  dependents?: number;
}

export function mapCustomer360Row(row: Record<string, ViewValue>): Customer360Row {
  return {
    id: String(row.id),
    displayName: String(row.display_name),
    kind: String(row.account_kind),
    identifier: row.identifier_last4 ? `***${String(row.identifier_last4)}` : "—",
    contact: String(row.primary_contact_masked ?? "—"),
    financialStatus: String(row.financial_status),
    riskLevel: String(row.risk_level),
    contracts: Number(row.contract_count ?? 0),
    subscriptions: Number(row.subscription_count ?? 0),
    agreements: Number(row.asset_agreement_count ?? 0),
    openInvoices: Number(row.open_invoice_count ?? 0),
    outstandingCents: String(row.outstanding_cents ?? 0),
    openTasks: Number(row.open_task_count ?? 0),
    ...(row.last_event_at ? { lastEventAt: String(row.last_event_at) } : {}),
    ...((row.customer_origin ?? row.origin)
      ? { customerOrigin: String(row.customer_origin ?? row.origin) }
      : {}),
    ...((row.responsible_seller ?? row.seller)
      ? { responsibleSeller: String(row.responsible_seller ?? row.seller) }
      : {}),
    ...((row.registration_status ?? row.registrationState)
      ? { registrationStatus: String(row.registration_status ?? row.registrationState) }
      : {}),
    ...((row.contract_status ?? row.contractState)
      ? { contractStatus: String(row.contract_status ?? row.contractState) }
      : {}),
    ...((row.access_status ?? row.accessState)
      ? { accessStatus: String(row.access_status ?? row.accessState) }
      : {}),
    ...((row.benefit_status ?? row.benefitState)
      ? { benefitStatus: String(row.benefit_status ?? row.benefitState) }
      : {}),
    ...(row.birth_date ? { birthDate: String(row.birth_date) } : {}),
    ...(row.address ? { address: String(row.address) } : {}),
    ...(row.dependent_count ? { dependents: Number(row.dependent_count) } : {}),
  };
}

function fakeCustomerToCustomer360(
  record: ReturnType<typeof getFakeCustomerRecord>,
): Customer360Row | null {
  if (!record) return null;
  return {
    id: record.id,
    displayName: record.displayName,
    kind: record.kind,
    identifier: record.identifier,
    contact: record.contact,
    financialStatus: record.financialStatus,
    riskLevel: record.riskLevel,
    contracts: record.contracts,
    subscriptions: record.subscriptions,
    agreements: record.agreements,
    openInvoices: record.openInvoices,
    outstandingCents: record.outstandingCents,
    openTasks: record.openTasks,
    ...(record.lastEventAt ? { lastEventAt: record.lastEventAt } : {}),
    customerOrigin: record.customerOrigin,
    responsibleSeller: resolveSellerName(record.responsibleSellerId),
    registrationStatus: record.registrationStatus,
    contractStatus: record.contractStatus,
    accessStatus: record.accessStatus,
    benefitStatus: record.benefitStatus,
    dependents: record.dependents,
  } satisfies Customer360Row;
}

export function getMockCustomer360ById(id: string) {
  return fakeCustomerToCustomer360(getFakeCustomerRecord(id));
}

function toCustomer360Presentation(customer: Customer360Row): Customer360Row {
  return {
    ...customer,
    financialStatus: customerStatusLabel(customer.financialStatus),
    registrationStatus: customerStatusLabel(customer.registrationStatus),
    contractStatus: customerStatusLabel(customer.contractStatus),
    accessStatus: customerStatusLabel(customer.accessStatus),
    benefitStatus: customerStatusLabel(customer.benefitStatus),
  };
}

export function useCustomer360Directory() {
  const queryScope = useDataQueryScope();
  const demoStoreVersion = useFakeCustomerStoreVersion();
  return useQuery({
    queryKey: queryScope.queryKey(["customer-360", env.useMocks ? demoStoreVersion : "persistent"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<Customer360Row[]> => {
      if (env.useMocks) {
        return listFakeCustomerRecords()
          .map((record) => fakeCustomerToCustomer360(record))
          .filter((customer): customer is Customer360Row => Boolean(customer))
          .map(toCustomer360Presentation);
      }
      const rows = await queryView<Record<string, ViewValue>>("app_customer_360_directory");
      return rows.map(mapCustomer360Row).map(toCustomer360Presentation);
    },
  });
}

export function useCustomer360Customer(id?: string) {
  const queryScope = useDataQueryScope();
  const demoStoreVersion = useFakeCustomerStoreVersion();
  return useQuery({
    queryKey: queryScope.queryKey([
      "customer-360",
      id,
      env.useMocks ? demoStoreVersion : "persistent",
    ]),
    enabled: Boolean(id) && queryScope.enabled,
    queryFn: async (): Promise<Customer360Row | null> => {
      if (!id) return null;
      if (env.useMocks) {
        const customer = getMockCustomer360ById(id);
        return customer ? toCustomer360Presentation(customer) : null;
      }
      const response = (await supabase
        .from("app_customer_360_directory")
        .select("*")
        .eq("id", id)
        .maybeSingle()) as { data: unknown; error: unknown };
      if (response.error) {
        throw response.error instanceof Error
          ? response.error
          : new Error("Não foi possível carregar o Customer 360.");
      }
      return response.data
        ? toCustomer360Presentation(mapCustomer360Row(response.data as Record<string, ViewValue>))
        : null;
    },
  });
}

export interface WorkflowQueueRow {
  id: string;
  workflowCode?: string;
  entityId?: string;
  workflow: string;
  entityType: string;
  state: string;
  status: string;
  pending: number;
  failed: number;
  updatedAt: string;
}
export function useWorkflowQueue() {
  const queryScope = useDataQueryScope();
  const fakeWorkflowVersion = useFakeWorkflowStoreVersion();
  return useQuery({
    queryKey: queryScope.queryKey([
      "workflow-queue",
      env.useMocks ? fakeWorkflowVersion : "persistent",
    ]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<WorkflowQueueRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "wf-1",
            workflowCode: "FUNERAL_ACTIVATION",
            entityId: "demo-subscription-1",
            workflow: "Ativação de plano",
            entityType: "SUBSCRIPTION",
            state: "AWAITING_PAYMENT",
            status: "ACTIVE",
            pending: 1,
            failed: 0,
            updatedAt: "20/09/2026 14:12",
          },
          {
            id: "wf-2",
            workflowCode: "VEHICLE_SALE_ACTIVATION",
            entityId: "demo-agreement-2",
            workflow: "Venda parcelada de veículo",
            entityType: "ASSET_AGREEMENT",
            state: "AWAITING_SIGNATURE",
            status: "ACTIVE",
            pending: 0,
            failed: 0,
            updatedAt: "20/09/2026 13:52",
          },
          {
            id: "wf-3",
            workflowCode: "MOBIMEDICAL_PROVISIONING",
            entityId: "demo-subscription-3",
            workflow: "Provisioning MobiMedical",
            entityType: "SUBSCRIPTION",
            state: "MANUAL_REVIEW",
            status: "ACTIVE",
            pending: 0,
            failed: 1,
            updatedAt: "20/09/2026 11:02",
          },
          ...listFakeWorkflowInstances(),
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_workflow_queue");
      return rows.map((row) => ({
        id: String(row.id),
        workflowCode: String(row.workflow_code ?? ""),
        entityId: String(row.entity_id ?? ""),
        workflow: String(row.workflow_name),
        entityType: String(row.entity_type),
        state: String(row.current_state_code),
        status: String(row.status),
        pending: Number(row.pending_actions ?? 0),
        failed: Number(row.failed_actions ?? 0),
        updatedAt: new Date(String(row.updated_at)).toLocaleString("pt-BR"),
      }));
    },
  });
}

export interface NotificationRow {
  id: string;
  customer: string;
  channel: string;
  destination: string;
  subject: string;
  status: string;
  scheduledAt: string;
  attempts: number;
}
export function useNotifications() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["notifications"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<NotificationRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "n-1",
            customer: "Carlos Oliveira",
            channel: "WHATSAPP",
            destination: "***0182",
            subject: "Pagamento confirmado",
            status: "DELIVERED",
            scheduledAt: "20/09/2026 14:22",
            attempts: 1,
          },
          {
            id: "n-2",
            customer: "Marcos Transportes Ltda",
            channel: "EMAIL",
            destination: "m***@empresa.com",
            subject: "Cobrança vencida",
            status: "QUEUED",
            scheduledAt: "20/09/2026 15:00",
            attempts: 0,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_notification_directory");
      return rows.map((row) => ({
        id: String(row.id),
        customer: String(row.customer ?? "—"),
        channel: String(row.channel),
        destination: String(row.destination_masked),
        subject: String(row.subject ?? "Notificação"),
        status: String(row.status),
        scheduledAt: new Date(String(row.scheduled_at)).toLocaleString("pt-BR"),
        attempts: Number(row.attempt_count ?? 0),
      }));
    },
  });
}

export interface DocumentTemplateRow {
  id: string;
  name: string;
  type: string;
  version: number;
  status: string;
  approval: string;
  approvals: string;
}
export function useDocumentTemplates() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["document-templates"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<DocumentTemplateRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "d-1",
            name: "Termo funerário",
            type: "FUNERAL_TERM",
            version: 1,
            status: "ACTIVE",
            approval: "IN_REVIEW",
            approvals: "1/3",
          },
          {
            id: "d-2",
            name: "Venda parcelada de veículo",
            type: "VEHICLE_SALE",
            version: 1,
            status: "DRAFT",
            approval: "DRAFT",
            approvals: "0/3",
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_document_template_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name),
        type: String(row.document_type),
        version: Number(row.version),
        status: String(row.status),
        approval: String(row.approval_status),
        approvals: `${Number(row.approved_count ?? 0)}/${Number(row.approval_count ?? 0)}`,
      }));
    },
  });
}

export interface ImportProfileRow {
  id: string;
  name: string;
  entityType: string;
  format: string;
  status: string;
  duplicatePolicy: string;
  mappings: number;
}
export function useImportProfiles() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["import-profiles"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<ImportProfileRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "i-1",
            name: "Clientes PF/PJ",
            entityType: "CUSTOMER",
            format: "CSV",
            status: "ACTIVE",
            duplicatePolicy: "REVIEW",
            mappings: 9,
          },
          {
            id: "i-2",
            name: "Vendas parceladas Celomar",
            entityType: "VEHICLE_SALE",
            format: "XLSX",
            status: "ACTIVE",
            duplicatePolicy: "REVIEW",
            mappings: 15,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_import_profile_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name),
        entityType: String(row.target_entity_type),
        format: String(row.source_format),
        status: String(row.status),
        duplicatePolicy: String(row.duplicate_policy),
        mappings: Number(row.mapping_count ?? 0),
      }));
    },
  });
}

export interface TenantHealthRow {
  tenantId: string;
  slug: string;
  status: string;
  production: boolean;
  healthy: number;
  failed: number;
  inbox: number;
  outbox: number;
  deadLetters: number;
  reconciliation: number;
  criticalIncidents: number;
}
export function useTenantHealth() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["tenant-health"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<TenantHealthRow[]> => {
      if (env.useMocks)
        return [
          {
            tenantId: "t-1",
            slug: "luto-cruzeiro",
            status: "PILOT",
            production: false,
            healthy: 4,
            failed: 0,
            inbox: 0,
            outbox: 1,
            deadLetters: 0,
            reconciliation: 0,
            criticalIncidents: 0,
          },
          {
            tenantId: "t-2",
            slug: "celomar-patrimonio",
            status: "PILOT",
            production: false,
            healthy: 2,
            failed: 0,
            inbox: 0,
            outbox: 0,
            deadLetters: 0,
            reconciliation: 1,
            criticalIncidents: 0,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("platform_tenant_health");
      return rows.map((row) => ({
        tenantId: String(row.tenant_id),
        slug: String(row.slug),
        status: String(row.status),
        production: Boolean(row.production_enabled),
        healthy: Number(row.healthy_integrations ?? 0),
        failed: Number(row.failed_integrations ?? 0),
        inbox: Number(row.inbox_pending ?? 0),
        outbox: Number(row.outbox_pending ?? 0),
        deadLetters: Number(row.dead_letters ?? 0),
        reconciliation: Number(row.reconciliation_issues ?? 0),
        criticalIncidents: Number(row.critical_incidents ?? 0),
      }));
    },
  });
}
